import { KeyboardEvent, useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Chat as ChatIcon, Close, RestartAlt, Send } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { chatWithAssistant, type AssistantState } from '../services/assistantService'
import { useLanguage } from '../contexts/LanguageContext'
import {
  extractReplyFromJsonLike,
  getDefaultQuickReplyPrompts,
  getLocalizedAssistantTypingLabel,
  getLocalizedChatPanelSubtitle,
  getLocalizedChatPanelTitle,
  getWelcomeMessage,
  isInstallationReference,
  normalizeAssistantReply
} from '../utils/chatWidgetUtils'

type ChatRole = 'user' | 'assistant'

type ChatMessage = {
  id: string
  role: ChatRole
  content: string
  createdAt: number
}

const DEFAULT_ASSISTANT_STATE: AssistantState = {
  workflow: 'none',
  step: 'idle'
}
const FLOATING_AI_HINT =
  'Internal floating chat instruction: Always reply in the same language as the latest user message. If the question is about installation, explicitly mention that AI-agent is available in the left sidebar and can also be opened from the button shown in this floating chat.'
const FLOATING_INSTALL_REDIRECT_HINT =
  'Internal floating chat instruction: This is floating-chat redirect mode. Reply in the same language as the user text. Do not start installation workflow, do not ask follow-up questions, do not ask to select project or device. Reply with a short routing message that says AI-agent is in the left sidebar and can be opened from the button in this floating chat.'

function isInstallWorkflowReply(
  replyText: string,
  assistantDirective: {
    state?: AssistantState
    ui?: {
      inputType?: 'text' | 'project_select' | 'device_select' | 'tag_select' | 'confirm'
    }
  } | null
): boolean {
  const hasInstallStep =
    assistantDirective?.state?.workflow === 'device_install' ||
    assistantDirective?.state?.workflow === 'ruuvitag_install' ||
    (assistantDirective?.state?.step !== undefined && assistantDirective.state.step !== 'idle')

  if (hasInstallStep) {
    return true
  }

  if (assistantDirective?.ui?.inputType && assistantDirective.ui.inputType !== 'text') {
    return true
  }

  const loweredReply = replyText.toLowerCase()

  return (
    loweredReply.includes('select a project') ||
    loweredReply.includes('select project') ||
    loweredReply.includes('select a device') ||
    loweredReply.includes('spine-, valve-, generator-') ||
    loweredReply.includes('valitse projekti') ||
    loweredReply.includes('valitse laite') ||
    loweredReply.includes('asennetaan')
  )
}

export default function FloatingChatWidget() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const { t } = useTranslation('installation')
  const { language } = useLanguage()
  const quickPrompts = getDefaultQuickReplyPrompts(false, language)
  const welcomeMessage = getWelcomeMessage(false, language)
  const panelTitle = getLocalizedChatPanelTitle(false, language)
  const panelSubtitle = getLocalizedChatPanelSubtitle(false, language)
  const assistantTypingLabel = getLocalizedAssistantTypingLabel(language)
  const selectActionLabel = t('chatWidgetSelectAction')
  const openChatAriaLabel = t('chatWidgetOpenChatAria')
  const newConversationAriaLabel = t('chatWidgetNewConversationAria')
  const closeChatAriaLabel = t('chatWidgetCloseChatAria')
  const messagePlaceholder = t('chatWidgetMessagePlaceholder')
  const sendMessageAriaLabel = t('chatWidgetSendMessageAria')
  const openAiAgentButtonLabel = t('chatWidgetOpenAIAgentButton', {
    defaultValue: t('chatWidgetPanelTitlePage')
  })
  const installRedirectFallbackReply = t('floatingChatOpenAIAgentForInstallations')

  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [showInstallCta, setShowInstallCta] = useState(false)
  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: welcomeMessage,
      createdAt: Date.now()
    }
  ])

  const openAIAgent = () => {
    setOpen(false)
    navigate('/ai-agent?source=floating-chat')
  }

  const buildFloatingReply = (message: string): string => {
    const lowered = message.toLowerCase()

    if (
      lowered.includes('asenn') ||
      lowered.includes('install') ||
      lowered.includes('ruuvi') ||
      lowered.includes('valve') ||
      lowered.includes('spine')
    ) {
      return installRedirectFallbackReply
    }

    if (
      lowered.includes('pikachat') ||
      lowered.includes('quick chat') ||
      (lowered.includes('what') && lowered.includes('chat'))
    ) {
      return welcomeMessage
    }

    return installRedirectFallbackReply
  }

  useEffect(() => {
    setMessages((previous) => {
      if (previous.length !== 1 || previous[0]?.id !== 'welcome') {
        return previous
      }

      return [
        {
          ...previous[0],
          content: welcomeMessage,
          createdAt: Date.now()
        }
      ]
    })
  }, [welcomeMessage])

  const buildInstallRedirectReply = async (userText: string): Promise<string> => {
    const response = await chatWithAssistant({
      message: `USER_TEXT: ${userText}`,
      sessionId,
      history: [
        {
          role: 'assistant',
          content: FLOATING_INSTALL_REDIRECT_HINT
        },
        {
          role: 'user',
          content: userText
        }
      ],
      state: DEFAULT_ASSISTANT_STATE,
      context: {
        language
      },
      selection: {
        selectedFromList: false,
        inputType: 'text'
      }
    })

    if (response.sessionId) {
      setSessionId(response.sessionId)
    }

    const rawReply = response.assistant?.reply || response.message
    const recoveredReply = extractReplyFromJsonLike(rawReply)
    const normalizedReply = normalizeAssistantReply(recoveredReply ?? rawReply)

    if (isInstallWorkflowReply(normalizedReply, response.assistant ?? null)) {
      return installRedirectFallbackReply
    }

    return normalizedReply
  }

  const resetConversation = () => {
    setInput('')
    setSessionId(undefined)
    setShowInstallCta(false)
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: welcomeMessage,
        createdAt: Date.now()
      }
    ])
  }

  const sendMessage = async (rawText: string) => {
    const trimmed = rawText.trim()

    if (!trimmed || isSending) {
      return
    }

    setShowInstallCta(false)

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      createdAt: Date.now()
    }

    setMessages((previous) => [...previous, userMessage])
    setInput('')

    const normalizedCommand = normalizeAssistantReply(trimmed).toLowerCase()
    const shouldOpenAIAgent =
      normalizedCommand === 'avaa ai-agentti' ||
      normalizedCommand === 'avaa ai agentti' ||
      normalizedCommand === 'ai-agentti' ||
      normalizedCommand === 'ai agentti' ||
      normalizedCommand === 'open ai agent' ||
      normalizedCommand === 'open ai-agent' ||
      normalizedCommand === 'ai agent' ||
      normalizedCommand === normalizeAssistantReply(openAiAgentButtonLabel).toLowerCase()

    if (shouldOpenAIAgent) {
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: installRedirectFallbackReply,
        createdAt: Date.now()
      }

      setMessages((previous) => [...previous, assistantMessage])
      openAIAgent()
      return
    }

    const history = [...messages, userMessage].map((entry) => ({
      role: entry.role,
      content: entry.content
    }))

    const requestHistory = [
      {
        role: 'assistant' as const,
        content: FLOATING_AI_HINT
      },
      ...history
    ]

    setIsSending(true)

    try {
      const response = await chatWithAssistant({
        message: trimmed,
        sessionId,
        history: requestHistory,
        state: DEFAULT_ASSISTANT_STATE,
        context: {
          language
        },
        selection: {
          selectedFromList: false,
          inputType: 'text'
        }
      })

      if (response.sessionId) {
        setSessionId(response.sessionId)
      }

      const assistantDirective = response.assistant
      const rawReply = assistantDirective?.reply || response.message || buildFloatingReply(trimmed)
      const recoveredReply = extractReplyFromJsonLike(rawReply)
      const normalizedReply = normalizeAssistantReply(recoveredReply ?? rawReply)

      const isInstallQuestion =
        assistantDirective?.intent === 'install_device' ||
        assistantDirective?.intent === 'install_ruuvitag' ||
        assistantDirective?.state?.workflow === 'device_install' ||
        assistantDirective?.state?.workflow === 'ruuvitag_install' ||
        isInstallationReference(trimmed)

      if (isInstallQuestion) {
        const installRedirectReply = await buildInstallRedirectReply(trimmed)

        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: installRedirectReply,
          createdAt: Date.now()
        }

        setMessages((previous) => [...previous, assistantMessage])
        setShowInstallCta(true)
        return
      }

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: normalizedReply,
        createdAt: Date.now()
      }

      setMessages((previous) => [...previous, assistantMessage])
    } catch (_error) {
      const isInstallQuestion = isInstallationReference(trimmed)

      if (isInstallQuestion) {
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: installRedirectFallbackReply,
          createdAt: Date.now()
        }

        setMessages((previous) => [...previous, assistantMessage])
        setShowInstallCta(true)
        return
      }

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: buildFloatingReply(trimmed),
        createdAt: Date.now()
      }

      setMessages((previous) => [...previous, assistantMessage])
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      void sendMessage(input)
    }
  }

  const handleQuickReply = (prompt: string) => {
    if (
      normalizeAssistantReply(prompt).toLowerCase() ===
      normalizeAssistantReply(openAiAgentButtonLabel).toLowerCase()
    ) {
      openAIAgent()
      return
    }

    void sendMessage(prompt)
  }

  useEffect(() => {
    const handleOpenAIAgentChat = () => {
      openAIAgent()
    }

    window.addEventListener('spine:open-ai-agent-chat', handleOpenAIAgentChat)

    return () => {
      window.removeEventListener('spine:open-ai-agent-chat', handleOpenAIAgentChat)
    }
  }, [])

  return (
    <Box
      sx={{
        position: 'fixed',
        right: isMobile ? 16 : 24,
        bottom: isMobile ? 88 : 72,
        zIndex: 1300
      }}
    >
      {!open ? (
        <Fab color="primary" aria-label={openChatAriaLabel} onClick={() => setOpen(true)}>
          <ChatIcon />
        </Fab>
      ) : (
        <Paper
          elevation={8}
          sx={{
            width: isMobile ? 'calc(100vw - 24px)' : 440,
            height: isMobile ? '76vh' : 560,
            maxHeight: isMobile ? 'calc(100vh - 110px)' : 'calc(100vh - 120px)',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 3
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5 }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {panelTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {panelSubtitle}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5}>
              <IconButton aria-label={newConversationAriaLabel} onClick={resetConversation}>
                <RestartAlt />
              </IconButton>
              <IconButton aria-label={closeChatAriaLabel} onClick={() => setOpen(false)}>
                <Close />
              </IconButton>
            </Stack>
          </Stack>

          <Divider />

          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              p: 2,
              bgcolor: theme.palette.background.default
            }}
          >
            <Stack spacing={1.5}>
              {messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '82%',
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor:
                        message.role === 'user'
                          ? theme.palette.primary.main
                          : theme.palette.background.paper,
                      color:
                        message.role === 'user'
                          ? theme.palette.primary.contrastText
                          : theme.palette.text.primary,
                      boxShadow: 1
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {message.content}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {!isSending && (
                <Box
                  sx={{
                    alignSelf: 'flex-start',
                    maxWidth: '95%',
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                    boxShadow: 1
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    {`${selectActionLabel}:`}
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {quickPrompts.map((prompt) => (
                      <Button
                        key={prompt}
                        variant="outlined"
                        size="small"
                        onClick={() => handleQuickReply(prompt)}
                        disabled={isSending}
                        sx={{ textTransform: 'none' }}
                      >
                        {prompt}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}

              {!isSending && showInstallCta && (
                <Box
                  sx={{
                    alignSelf: 'flex-start',
                    display: 'flex',
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                    boxShadow: 1
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={openAIAgent}
                    sx={{ textTransform: 'none' }}
                  >
                    {openAiAgentButtonLabel}
                  </Button>
                </Box>
              )}

              {isSending && (
                <Box
                  sx={{
                    alignSelf: 'flex-start',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                    boxShadow: 1
                  }}
                >
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    {assistantTypingLabel}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          <Divider />

          <Stack direction="row" spacing={1} sx={{ p: 1.5 }} alignItems="flex-end">
            <TextField
              fullWidth
              size="small"
              placeholder={messagePlaceholder}
              multiline
              maxRows={4}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSending}
            />
            <IconButton
              color="primary"
              aria-label={sendMessageAriaLabel}
              onClick={() => void sendMessage(input)}
              disabled={!input.trim() || isSending}
            >
              <Send />
            </IconButton>
          </Stack>
        </Paper>
      )}
    </Box>
  )
}
